import React, { FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { createRoot } from 'react-dom/client';
import { FormattedMessage } from 'react-intl';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';

import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { BaseStyle, BaseStyleEnums, ButtonType, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import TranslationLoader from '@nhl/nhl-react-ui/lib/TranslationLoader';
import { usePolling } from '@nhl/react-hooks/lib/usePolling';

import { DraftState, ThemeName } from '~/core/enums';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { getMillisecondsUntilTime } from '~/core/utils';
import { defaultLocationData, useLocationQuery } from '~/redux/ducks/location';
import configureStore from '~/redux/store';

import BaseCarousel from '../baseCarousel/BaseCarousel';
import CarouselContextProvider, { CarouselContext } from '../baseCarousel/CarouselContextProvider';
import { SlideType } from '../baseCarousel/enums';
import { SlideData, SlideMetadata } from '../baseCarousel/types';
import useFetchConfig from '../hooks/useFetchConfig';
import useLuxonDefaultLocale from '../hooks/useLuxonDefaultLocale';

import { useDraftPicksQuery } from './api';
import DraftLinks from './components/draftLinks/DraftLinks';
import DraftPickSlide from './components/draftPickSlide/DraftPickSlide';
import { DRAFT_PICK_SLIDE_WIDTH } from './components/draftPickSlide/styles';
import Header from './components/header/Header';
import Skeleton from './components/skeleton/Skeleton';
import { DraftPickState } from './enums';
import { DraftPick } from './types';

import {
    CarouselRow,
    CurrentPickButton,
    DRAFT_PICK_SLIDE_HEIGHT,
    DraftMiniTrackerWrapper,
    LinksRow,
    NextNavControl,
    PrevNavControl,
    RoundRow,
    SKINNY_SLIDE_WIDTH,
    SkinnySlide,
} from './styles';

type Props = {
    language: ShortCodeEnums.Language;
    theme?: ThemeName;
    showDraftCentralLink?: boolean;
    showFullTrackerLink?: boolean;
};

const DraftMiniTrackerComponent: FC<Props> = ({
    language,
    theme = ThemeName.dark,
    showDraftCentralLink = true,
    showFullTrackerLink = true,
}) => {
    useLuxonDefaultLocale(language);

    const [currentPickIndex, setCurrentPickIndex] = useState<number>(0);

    const carouselContext = useContext(CarouselContext);

    const config = useFetchConfig();
    const screenSize = useSizeWithThrottle();

    const {
        data: draftResponse,
        isInitialLoad: isDraftLoading,
        refetch: refetchDraftPicks,
    } = useDraftPicksQuery({
        baseUrl: config?.webAPIBaseURL,
        isEnabled: config !== undefined,
    });

    const { data: countryCode = defaultLocationData, isLoading: isLocationLoading } = useLocationQuery(
        {
            baseUrl: config?.webAPIBaseURL,
        },
        { skip: config === undefined }
    );

    // If we're showing the controls then in order to actually fully display a specific pick we need to set the prior
    // pick as the active one. This is because the controls show on top of the carousel itself. At the beginning and
    // end of the carousel this isn't necessary because the prev/next buttons respectively get hidden at those points.
    // Additionally, we don't show the controls on mobile screen sizes, instead relying on mouse/touch dragging, so
    // this isn't necessary there either.
    const getCorrectActiveIndex = useCallback(
        (index: number, maxIndex: number): number =>
            index === 0 || index === maxIndex || !carouselContext.shouldShowControls ? index : index - 1,
        [carouselContext]
    );

    const isDraftActive = ![DraftState.OVER_DAY_1, DraftState.OVER, DraftState.FUTURE].includes(draftResponse.state);

    const millisToDraftStart = useMemo<number | undefined>(
        () => getMillisecondsUntilTime(draftResponse.broadcastStartTimeUTC),
        [draftResponse]
    );

    usePolling(refetchDraftPicks, isDraftActive, millisToDraftStart);

    const slideData = useMemo<SlideData<DraftPick>>(() => {
        const slides: Array<ReactNode> = [];
        const slideMetadata: Array<SlideMetadata<DraftPick>> = [];

        draftResponse.picks?.forEach((pick, index, picks) => {
            // if the next pick is on the clock, then this pick is "active", or the most recently selected pick
            // or, if this is the last pick in the list and it is "confirmed" then this pick is active.
            let isActive = false;

            if (isDraftActive) {
                isActive =
                    (index < picks.length - 1 && picks[index + 1].state === DraftPickState.ON_THE_CLOCK) ||
                    (index === picks.length - 1 && pick.state === DraftPickState.CONFIRMED);
            }

            if (isActive) {
                setCurrentPickIndex(index);
            }

            slides.push(
                <DraftPickSlide
                    accentColor={draftResponse.uiAccentColor}
                    isActive={isActive}
                    language={language}
                    key={pick.overallPick}
                    pick={pick}
                />
            );

            slideMetadata.push({
                key: pick.overallPick,
                originalData: pick,
                type: SlideType.DRAFT_PICK,
            });
        });

        // if the draft isn't active then we want to make sure the current pick gets set to 0. This won't affect
        // anything if the user has been scrolling around, but it will allow the carousel to auto-scroll back to the
        // start once the draft is in an over state.
        if (!isDraftActive) {
            setCurrentPickIndex(0);
        }

        return {
            slides,
            slideMetadata,
        };
    }, [draftResponse, isDraftActive, language]);

    useEffect(() => {
        if (!carouselContext.hasUserChangedIndex && currentPickIndex !== undefined) {
            carouselContext.setActiveIndex(getCorrectActiveIndex(currentPickIndex, draftResponse.picks?.length - 1));
        }
    }, [carouselContext, currentPickIndex, draftResponse.picks?.length, getCorrectActiveIndex]);

    const loadTranslations = async (language: string) => {
        if (config !== undefined) {
            const response = await fetch(`${config.translationsBaseURL}/${language}.json?rel=${config.appId}`);
            return await response.json();
        }
    };

    return (
        <ThemeProvider theme={theme === ThemeName.dark ? BaseStyle.DarkTheme : BaseStyle.LightTheme}>
            <BaseStyle.RootStyle>
                <DraftMiniTrackerWrapper>
                    {isDraftLoading || isLocationLoading || config === undefined ? (
                        <Skeleton />
                    ) : (
                        <TranslationLoader
                            callback={loadTranslations}
                            activeLanguage={language}
                            showLoader={false}
                            customLoader={<Skeleton />}
                        >
                            <Header
                                draft={draftResponse}
                                countryCode={countryCode}
                                language={language}
                                showDraftCentralLink={showDraftCentralLink}
                                showFullTrackerLink={showFullTrackerLink}
                            />
                            <RoundRow>
                                <FormattedMessage
                                    description="Text describing which round of the draft is currently showing, eg 'Round 1'"
                                    defaultMessage="Round {round}"
                                    values={{ round: draftResponse.round }}
                                />
                                {carouselContext.hasUserChangedIndex && isDraftActive && (
                                    <CurrentPickButton
                                        variant={ButtonType.Text}
                                        onClick={() => {
                                            carouselContext.setActiveIndex(
                                                getCorrectActiveIndex(currentPickIndex, slideData.slides.length - 1)
                                            );
                                            carouselContext.setHasUserChangedIndex(false);
                                        }}
                                    >
                                        <FormattedMessage
                                            description="Text for a button that will scroll the current pick into view"
                                            defaultMessage="Current Pick"
                                        />
                                    </CurrentPickButton>
                                )}
                            </RoundRow>
                            <CarouselRow>
                                <BaseCarousel<DraftPick>
                                    height={DRAFT_PICK_SLIDE_HEIGHT}
                                    leftPadding={16}
                                    overrideAnimation
                                    regularSlideWidth={DRAFT_PICK_SLIDE_WIDTH}
                                    renderNextButton={(slideNext) =>
                                        carouselContext.shouldShowControls &&
                                        slideData.slides.length >
                                            carouselContext.activeIndex + carouselContext.visibleSlides && (
                                            <SkinnySlide $isLeftControl={false}>
                                                <NextNavControl onClick={slideNext}>
                                                    <ChevronRightRounded />
                                                </NextNavControl>
                                            </SkinnySlide>
                                        )
                                    }
                                    renderPrevButton={(slidePrev) =>
                                        carouselContext.shouldShowControls &&
                                        carouselContext.activeIndex !== 0 && (
                                            <SkinnySlide $isLeftControl>
                                                <PrevNavControl onClick={slidePrev}>
                                                    <ChevronLeftRounded />
                                                </PrevNavControl>
                                            </SkinnySlide>
                                        )
                                    }
                                    rightPadding={16}
                                    slideData={slideData}
                                    skinnySlideWidth={SKINNY_SLIDE_WIDTH}
                                />
                            </CarouselRow>
                            {screenSize.width < BaseStyleEnums.ScreenWidthBreakpoints.medium && (
                                <LinksRow>
                                    <DraftLinks
                                        language={language}
                                        showDraftCentralLink={showDraftCentralLink}
                                        showFullTrackerLink={showFullTrackerLink}
                                    />
                                </LinksRow>
                            )}
                        </TranslationLoader>
                    )}
                </DraftMiniTrackerWrapper>
            </BaseStyle.RootStyle>
        </ThemeProvider>
    );
};

const WrappedDraftMiniTrackerComponent: FC<Props> = (props) => (
    <CarouselContextProvider>
        <DraftMiniTrackerComponent {...props} />
    </CarouselContextProvider>
);

export default WrappedDraftMiniTrackerComponent;

type DraftMiniTrackerArgs = Partial<Props> & {
    rootElementId: string;
};

export const DraftMiniTracker = async ({
    rootElementId,
    language = ShortCodeEnums.Language.English,
    theme = ThemeName.dark,
    showDraftCentralLink = true,
    showFullTrackerLink = true,
}: DraftMiniTrackerArgs) => {
    const rootElement = document.getElementById(rootElementId);

    if (rootElement !== null) {
        const root = createRoot(rootElement);
        const store = configureStore();

        root.render(
            <Provider store={store}>
                <WrappedDraftMiniTrackerComponent
                    language={language}
                    theme={theme}
                    showDraftCentralLink={showDraftCentralLink}
                    showFullTrackerLink={showFullTrackerLink}
                />
            </Provider>
        );
    }
};
